import React from 'react';
import * as Routes from '@/modules/routes';
import {
  Table, TableBody, TableCell, TableHeader, TableRow,
} from '@/components/ui/Table';
import useFlags from '@/hooks/useFlags';
import cn from '@/utils/cn';
import CollapsibleSection from './CollapsibleSection';
import TruncationNotice from './TruncationNotice';
import type { TransactionRow } from './types';

const COLUMNS = ['PO number', 'Transaction ID', 'Contact', 'Subscriber', 'Invoiced', 'Transactions'];

// Same header band and row dividers as SPADataTable; hand-rolled because the rows are static.
export const headerCellClass = cn(
  'tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-heading',
  'tw-bg-spa-bg-tertiary tw-border-0 tw-border-b-2 tw-border-solid tw-border-spa-border',
);

export const cellClass = cn(
  'tw-px-3 tw-py-2 tw-text-sm tw-align-top tw-text-spa-primary',
  'tw-border-0 tw-border-b tw-border-solid tw-border-spa-border-secondary',
);

export const linkClass = cn(
  'tw-text-spa-accent-text tw-no-underline hover:tw-text-spa-accent-text-hover hover:tw-underline',
);

// invoiced_on is a bare date — new Date('2024-05-14') would parse as UTC midnight and render
// the previous day in western timezones.
export const formatInvoicedOn = (iso: string | null) => {
  if (!iso) return '—';

  const [year, month, day] = iso.split('-').map(Number);

  return new Date(year, month - 1, day).toLocaleDateString();
};

export const filteredTransactionsPath = (subscriberId: number, poNumber: string) => (
  Routes.admin_subscriber_subscriptions_path(subscriberId, {
    initialFilters: { 0: { operatorValue: 'eq', propertyValue: 'purchase_order_number', value: poNumber } },
  })
);

const TransactionResultRow = ({ row }: { row: TransactionRow }) => (
  <TableRow className="[&:last-child_td]:tw-border-b-0">
    <TableCell className={cellClass}>
      {row.subscriber ? (
        <a
          className={cn(linkClass, 'tw-whitespace-nowrap tw-font-semibold')}
          href={filteredTransactionsPath(row.subscriber.id, row.number)}
        >
          {row.number}
        </a>
      ) : row.number}
    </TableCell>
    <TableCell className={cellClass}>{row.order_number || '—'}</TableCell>
    <TableCell className={cellClass}>
      {row.admin_email}
      {row.purchaser_name && (
        <span className="tw-block tw-text-xs tw-text-spa-tertiary">{row.purchaser_name}</span>
      )}
    </TableCell>
    <TableCell className={cellClass}>
      {row.subscriber ? (
        <a className={linkClass} href={Routes.admin_subscriber_home_path(row.subscriber.id)}>
          {row.subscriber.name}
          {row.subscriber.customer_number && (
            <span className="tw-block tw-text-xs tw-text-spa-tertiary">
              Customer #{row.subscriber.customer_number}
            </span>
          )}
        </a>
      ) : '—'}
    </TableCell>
    <TableCell className={cn(cellClass, 'tw-whitespace-nowrap')}>{formatInvoicedOn(row.invoiced_on)}</TableCell>
    <TableCell className={cn(cellClass, 'tw-tabular-nums')}>{row.subscriptions_count}</TableCell>
  </TableRow>
);

interface TransactionResultsProps {
  limit: number;
  open: boolean;
  transactions: TransactionRow[];
  truncated: boolean;
  onOpenChange: (open: boolean) => void;
}

const TransactionResults = ({
  limit, open, transactions, truncated, onOpenChange,
}: TransactionResultsProps) => {
  const { accessibilityReflow } = useFlags();
  const count = transactions.length;

  return (
    <CollapsibleSection
      id="transactions"
      open={open}
      summary={`(${count}${truncated ? '+' : ''} order${count === 1 && !truncated ? '' : 's'})`}
      title="Transactions"
      onOpenChange={onOpenChange}
    >
      {transactions.length === 0 ? (
        <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">
          No orders have this contact email.
        </p>
      ) : (
        <div className="tw-overflow-x-auto">
          <Table className={cn('tw-w-full tw-border-collapse', accessibilityReflow && 'tw-min-w-[40rem]')}>
            <TableHeader>
              <TableRow>
                {COLUMNS.map(column => (
                  <TableCell key={column} header className={headerCellClass}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map(row => <TransactionResultRow key={row.id} row={row} />)}
            </TableBody>
          </Table>
          {truncated && <TruncationNotice label="orders" limit={limit} />}
        </div>
      )}
    </CollapsibleSection>
  );
};

export default TransactionResults;
